import { useState } from "react";
import { Bug, Lightbulb, MessageCircle, MessageSquarePlus, Send } from "lucide-react";
import { useDemo } from "@/store/DemoStore";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { btn } from "@/components/common";
import { cn } from "@/lib/utils";

const TYPES = [["bug", "Bug", Bug], ["suggestion", "Suggestion", Lightbulb], ["feedback", "Feedback", MessageCircle]];

export default function FeedbackButton({ className, variant = "floating" }) {
  const { submitFeedback } = useDemo();
  const [open, setOpen] = useState(false);
  const [type, setType] = useState("bug");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!message.trim()) return;
    setBusy(true);
    const r = await submitFeedback({ type, message: message.trim(), page: window.location.pathname });
    setBusy(false);
    if (r) { setMessage(""); setType("bug"); setOpen(false); }
  };

  const trigger = variant === "icon" ? (
    <button onClick={() => setOpen(true)} data-testid="feedback-icon-btn" aria-label="Send feedback"
      className={cn("grid h-10 w-10 place-items-center rounded-full text-navy-700 transition-colors hover:bg-slate-100", className)}>
      <MessageSquarePlus className="h-5 w-5" />
    </button>
  ) : (
    <button onClick={() => setOpen(true)} data-testid="feedback-fab" aria-label="Send feedback"
      className={cn("fixed z-40 h-14 w-14 place-items-center rounded-full bg-navy-900 text-white shadow-lg shadow-navy-900/30 transition-transform hover:scale-105 active:scale-95", className)}>
      <MessageSquarePlus className="h-6 w-6" />
    </button>
  );

  return (
    <>
      {trigger}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="feedback-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl text-navy-900">Share Your Feedback</DialogTitle>
            <DialogDescription>Report a bug, suggest an idea, or tell us anything — it goes straight to the Topsail team.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-2" data-testid="feedback-type">
              {TYPES.map(([v, l, Icon]) => (
                <button key={v} onClick={() => setType(v)} data-testid={`feedback-type-${v}`}
                  className={cn("flex flex-col items-center gap-1.5 rounded-xl border px-2 py-3 text-xs font-semibold transition-colors", type === v ? "border-navy-900 bg-navy-50 text-navy-900" : "border-slate-200 text-slate-500 hover:bg-slate-50")}>
                  <Icon className="h-5 w-5" />{l}
                </button>
              ))}
            </div>
            <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={5} data-testid="feedback-message" placeholder="Tell us what's on your mind…"
              className="w-full resize-y rounded-xl border border-slate-200 bg-white px-3.5 py-3 text-base text-navy-900 outline-none focus:border-navy-500 focus:ring-2 focus:ring-navy-100 sm:text-sm" />
            <button disabled={busy || !message.trim()} onClick={submit} data-testid="feedback-submit" className={`${btn.primary} w-full`}>{busy ? "Sending…" : <>Send feedback<Send className="h-4 w-4" /></>}</button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
